/* Common Imports */

import { styled } from "@mui/system";
import React from "react";
/* Redux Imports */

/* Component Imports */

import { Close } from "@mui/icons-material";
import {
	Alert,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	Snackbar,
	TextField,
	useTheme,
} from "@mui/material";
import { ObjectId } from "bson";
import { UpdatePropertyTitleThunk } from "../../../redux-magic/thunks";
import BeegruButton from "../../common-components/buttons/BeegruButton";

/* Icon Imports */

/* Styled Components */

const CustomDialog = styled(Dialog)(({ theme }) => ({
	padding: "1rem 1rem 1rem 1rem",
	width: "100%",
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	padding: "0.5rem 1rem 0.5rem 1rem",
	fontSize: "1.125rem",
	background: theme.palette.background.paper,
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	alignItems: "center",
	width: "100%",
	gap: "0rem",
	padding: "2rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
	[theme.breakpoints.down("sm")]: {
		flexDirection: "column",
	},
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-end",
	alignItems: "center",
	width: "100%",
	gap: "1rem",
	padding: "0rem 1rem 1rem 1rem",
	background: theme.palette.background.paper,
}));

const PropertyTitleEditModal = ({
	session,
	open,
	handleClose,
	dispatch,
	phoneNumber,
	countryCode,
	property_id,
	editor_is_business_profile,
	editor_user_id,
	editor_business_profile_id,
	property_title,
	slugEdited,
	setSlugEdited,
}: {
	session: any;
	open: boolean;
	handleClose: any;
	dispatch: Function;
	phoneNumber: string;
	countryCode: string;
	property_id: string | ObjectId;
	editor_is_business_profile: boolean;
	editor_user_id: string | ObjectId;
	editor_business_profile_id: string | ObjectId;
	property_title: string;
	slugEdited: boolean;
	setSlugEdited: Function;
}) => {
	const theme = useTheme();

	const [propertyTitle, setPropertyTitle] = React.useState<string>(property_title);
	const [loading, setLoading] = React.useState<boolean>(false);

	const handleModalClose = () => {
		handleClose();
	};

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const handleCloseSnackbar = (event: any, reason: any) => {
		if (reason === "clickaway") {
			return;
		}
		setOpenSnackbar(false);
	};

	const handleModalSubmit = async () => {
		await dispatch(
			UpdatePropertyTitleThunk({
				sessionId: session ? (session.session_id ? session.session_id : "") : "",
				phoneNumber: phoneNumber,
				countryCode: countryCode,
				property_id: property_id,
				editor_is_business_profile: editor_is_business_profile,
				editor_user_id: editor_user_id,
				editor_business_profile_id: editor_business_profile_id,
				property_title: propertyTitle,
				slug: propertyTitle.toLowerCase().replace(/\W+/g, "-").replace(/^-/, "").replace(/\-$/, ""),
			}),
		);
		handleClose();
		setSlugEdited(true);
		setLoading(false);
		setOpenSnackbar(true);
	};

	return (
		<React.Fragment>
			<Snackbar
				anchorOrigin={{ vertical: "top", horizontal: "center" }}
				open={openSnackbar}
				autoHideDuration={3000}
				onClose={handleCloseSnackbar}
			>
				<Alert
					onClose={(e) => {
						handleCloseSnackbar(e, "");
					}}
					severity="success"
					variant="filled"
					sx={{ width: "100%", borderRadius: "0.75rem" }}
				>
					Property title updated successfully!
				</Alert>
			</Snackbar>
			<CustomDialog
				open={open}
				onClose={handleModalClose}
				maxWidth="xs"
				fullWidth={true}
				disableScrollLock={true}
			>
				<CustomDialogTitle>
					Edit Property Title
					<IconButton
						aria-label="close"
						onClick={handleModalClose}
					>
						<Close />
					</IconButton>
				</CustomDialogTitle>

				<CustomDialogContent>
					<TextField
						label="Property Name *"
						placeholder="Enter property name"
						required={true}
						value={propertyTitle}
						variant="outlined"
						onChange={(event: any) => {
							setPropertyTitle(event.target.value);
						}}
						sx={{ width: "100%", marginTop: "1rem" }}
					/>
				</CustomDialogContent>

				<CustomDialogActions>
					<BeegruButton
						flavor="primary"
						variant="outlined"
						onClick={handleModalClose}
					>
						Cancel
					</BeegruButton>
					{!loading ? (
						<BeegruButton
							flavor="primary"
							variant="contained"
							disabled={propertyTitle === property_title || propertyTitle === "" || loading}
							onClick={() => {
								handleModalSubmit();
								setLoading(true);
							}}
						>
							Submit
						</BeegruButton>
					) : (
						<CircularProgress size={24} />
					)}
				</CustomDialogActions>
			</CustomDialog>
		</React.Fragment>
	);
};

export default PropertyTitleEditModal;
